import { Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { AppContextService, ApiGatewayService, ActivityMonitorService } from '@drc-eca/eca-components-lib';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { map, switchMap, catchError, tap, takeUntil } from 'rxjs/operators';
import { of, Observable, Subject, from } from 'rxjs';
import { OktaService } from '../../core/services/okta.service';
import { environment } from '../../../environments/environment';

const ECA_OKTA_CLIENT = 'eca-okta-client';
const ECA_OKTA_NAVIGATE = 'eca-okta-navigate';
const ECA_OKTA_TIMEOUT = 'eca-okta-timeout';
const OKTA_TOKEN_STORAGE = 'okta-token-storage';

@Component({
    selector: 'app-logout',
    templateUrl: './logout.component.html',
    styleUrls: ['./logout.component.scss']
})
export class LogoutComponent implements OnInit, OnDestroy {
  private apiGatewayService = inject(ApiGatewayService);
  private appContextService = inject(AppContextService);
  private activityMonitorService = inject(ActivityMonitorService);
  private oktaService = inject(OktaService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private zone = inject(NgZone);

  private destroy = new Subject()

  ngOnInit() {
    this.activityMonitorService.stopMonitoring();
    const navigatedToOkta = sessionStorage.getItem(ECA_OKTA_NAVIGATE);
    if (!navigatedToOkta) {
      const timeout: boolean = this.route.snapshot.queryParams['timeout'] === 'true' || !!sessionStorage.getItem(ECA_OKTA_TIMEOUT);
      const oktaTokens = sessionStorage.getItem(OKTA_TOKEN_STORAGE);
      let client: string;

      this.route.params.pipe(
        switchMap(params => this.getProductCode(params)),
        tap(productCode => {
          client = productCode;
          this.appContextService.setSelectedProductCode(productCode)
        }),
        map(productCode => this.getReturnUrl(productCode)),
        switchMap((returnUrl) => this.logoutEca().pipe(map(() => returnUrl))),
        // This might navigate to Okta to logout
        switchMap(returnUrl => this.logoutOkta(returnUrl, timeout)),
        takeUntil(this.destroy)
      ).subscribe((returnUrl) => {
        // Try to detect if we are navigating to Okta from logoutOkta
        if (!oktaTokens) {
          this.restore(client, timeout);
          this.navigate(returnUrl, timeout);
        }
      });
    } else {
      // Okta has routed back to us so simplify routing to login
      sessionStorage.removeItem(ECA_OKTA_NAVIGATE);
      const productCode = sessionStorage.getItem(ECA_OKTA_CLIENT) as string;
      const timeout = !!sessionStorage.getItem(ECA_OKTA_TIMEOUT);
      const returnUrl = this.getReturnUrl(productCode);
      this.restore(productCode, timeout);
      this.navigate(returnUrl, timeout);
    }
  }

  private restore(productCode?: string, timeout?: boolean) {
    sessionStorage.clear();
    if (productCode) {
      sessionStorage.setItem(ECA_OKTA_CLIENT, productCode);
    }
    if (timeout) {
      sessionStorage.setItem(ECA_OKTA_TIMEOUT, String(timeout));
    }
  }

  private getProductCode(params: Params) {
    // if product code is specified in the URL, use it
    if (params.productCode) {
      return of(params.productCode);
    } else {
      // else, lookup previously selected product code
      return this.appContextService.getSelectedProductCode().pipe(
        map(productCode => productCode)
      );
    }
  }

  private getReturnUrl(productCode?: string) {
    return productCode ? ['login', productCode] : ['login'];
  }

  private logoutOkta(returnUrl: string[], timeout?: boolean): Observable<any> {
    sessionStorage.setItem(ECA_OKTA_NAVIGATE, 'true');

    const redirectUrl = `${environment.okta.clientConfig.redirectUri}/${location.pathname}#/logout`.replace('//all', '/all');
    if (returnUrl[1]) {
      sessionStorage.setItem(ECA_OKTA_CLIENT, returnUrl[1]);
    }
    if (timeout) {
      sessionStorage.setItem(ECA_OKTA_TIMEOUT, 'true');
    }

    return from(this.actualLogoutOkta(redirectUrl)).pipe(map(() => returnUrl));
  }

  private logoutEca() {
    const apiConfig = this.apiGatewayService.makeApiConfigString();
    const url = `eca-security-service/${apiConfig}/v0/logout`;

    return this.appContextService.getToken().pipe(
      switchMap(token => this.apiGatewayService.post<any>(url, { token: token })),
      catchError(() => of(undefined)),
      tap(() => this.appContextService.setToken(null))
    );
  }

  private async actualLogoutOkta(redirectUrl: string) {
    try {
      await this.oktaService.logout(redirectUrl);
    } catch (err) {
      // ignore errors
      console.error(err);
    }
  }

  private navigate(url: string[], timeout: boolean) {
    this.zone.run(() => {
      if (timeout) {
        this.router.navigate(url, { queryParams: { timeout: true } });
      } else {
        this.router.navigate(url);
      }
    })
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
  }
}
